<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcBillingList',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    companyId: {
      type: String,
      required: false,
    },
    clientId: {
      type: String,
      required: false,
    },
    rentalId: {
      type: String,
      required: false,
    },
    contractId: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    return {
      company_id: props.companyId,
      client_id: props.clientId,
      rental_id: props.rentalId,
      contract_id: props.contractId,
    };
  };
  const onCreateInvoicesPayload = (...args) => {
    return {
      items: selectedItems.value.map((p) => ({ id: p.id })),
    };

    return $imodal.value
      .show({ name: 'CompleteInvoicesAlt' })
      .then((result) => {
        if (!result || result.cancel || !result.value) {
          return;
        }
        return selectedItems.value.map((p) => ({
          ...result.value,
          invoice_id: p.id,
        }));
      });
  };
  const onCreateBillingPlan = (...args) => {
    return {
      items: selectedItems.value.map((p) => ({ id: p.id })),
    };

    return $imodal.value.show({ name: 'CreateBillingPlan' }).then((result) => {
      if (!result || result.cancel || !result.value) {
        return;
      }
      return selectedItems.value.map((p) => ({
        ...result.value,
        invoice_id: p.id,
      }));
    });
  };
  const getDownloadViewsPayload = (...args) => {
    return {
      identities: _.chain(selectedItems.value)
        .filter((p) => p.invoice && p.invoice.id)
        .map((p) => p.invoice.id)
        .uniq()
        .value(),
    };
  };

  const gridName = ref('billingList');
  const messages = ref({
    title: $fn.value.pgettext('billingList|Title', 'Billing list'),
    exportFilename: $fn.value.pgettext(
      'billingList|Export|Filename',
      'billing-list'
    ),
  });
  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_7315092382398562_columns = computed(() => [
    {
      field: 'invoice_date',
      name: $fn.value.pgettext('BillingList', 'Billing due date'),
      type: 'date',
    },
    {
      field: 'invoice',
      name: $fn.value.pgettext('BillingList', 'Invoice'),
      sortPath: 'invoice.invoice_number',
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <a href="#" onclick="return false;">\n\
      <strong title="{{ $value.invoice_number }}">{{ $value.invoice_number }}</strong>\n\
    </a>\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'due_date',
      name: $fn.value.pgettext('BillingList', 'Payment due date'),
      type: 'date',
    },
    {
      name: $fn.value.pgettext('BillingList', 'Period start'),
      field: 'period.start',
      type: 'date',
    },
    {
      name: $fn.value.pgettext('BillingList', 'Period end'),
      field: 'period.end',
      type: 'date',
    },
    {
      field: 'company',
      name: $fn.value.pgettext('BillingList', 'Company'),
      sortPath: 'company.code',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'company.id',
            },
          },
        ],
      },
    },
    {
      field: 'partner',
      name: $fn.value.pgettext('BillingList', 'Partner'),
      sortPath: 'partner.code',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'partner.id',
            },
          },
        ],
      },
    },
    {
      field: 'contract',
      name: $fn.value.pgettext('BillingList', 'Contract'),
      sortPath: 'contract.contract_number',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.contract_number }}">{{ $value.contract_number }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'contract.id',
            },
          },
        ],
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('BillingList', 'Rental'),
      sortPath: 'rental.name',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.name }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental.id',
            },
          },
        ],
      },
    },
    {
      field: 'description',
      name: $fn.value.pgettext('BillingList', 'Description'),
      type: 'command',
    },
    {
      field: 'billing_category',
      name: $fn.value.pgettext('BillingList', 'Billing category'),
    },
    {
      field: 'notes',
      name: $fn.value.pgettext('BillingList', 'Notes'),
    },
    {
      field: 'quantity',
      name: $fn.value.pgettext('BillingList', 'Quantity'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('BillingList', 'Unit'),
    },
    {
      field: 'unit_price',
      name: $fn.value.pgettext('BillingList', 'Price'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('BillingList', 'Currency'),
    },
    {
      field: 'discount',
      name: $fn.value.pgettext('BillingList', 'Discount'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'net_amount',
      name: $fn.value.pgettext('BillingList', 'Net'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'tax_code',
      name: $fn.value.pgettext('BillingList', 'Tax code'),
    },
    {
      field: 'exchange_rate',
      name: $fn.value.pgettext('BillingList', 'Exchrate'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <span title="{{ $value.value }}">{{ $value.value }}</span>\n\
\n\
    {{~#if $value.date }}\n\
      <span>&middot; {{ $value.date }}</span>\n\
    {{~/if }}\n\
\n\
    {{~#if $value.source }}\n\
      <span>&middot; {{ $value.source }}</span>\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'intermediated_service',
      name: $fn.value.pgettext('BillingList', 'Intermediated Service'),
      formatter: 'checkmark',
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    messages: messages.value,
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_columnDefaults: b_7315092382398562_columnDefaults.value,
    b_7315092382398562_columns: b_7315092382398562_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('list_billing_list_item', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'messages' },
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_columnDefaults', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_columns', nodeId: '7315092382398562' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      messages,
      selectedItems,
      ds,
      ux,
      b_7315092382398562_columnDefaults,
      b_7315092382398562_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.companyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('companyId');
    }
  );
  watch(
    () => props.clientId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('clientId');
    }
  );
  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );
  watch(
    () => props.contractId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contractId');
    }
  );

  const act_7315092382398562_setData_6y5xaevsc_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7315092382398562_script_scd90432q_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_7315092382398562_script_scd90432q_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_scd90432q_value_callable($event);
      },
    };
  };
  const act_7315092382398562_showModal_mcph983y0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BillingListItem',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_7315092382398562_command_cozt21l9s_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'description';
  };
  const act_7315092382398562_script_yfupfl8fg_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_7315092382398562_script_yfupfl8fg_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_yfupfl8fg_value_callable($event);
      },
    };
  };
  const act_7315092382398562_showModal_f34z32y95_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.invoice.id,
      },
    };
  };
  const inst_evh_7315092382398562_command_fyxiofnxy_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'invoice' && !!$event.data.data.invoice;
  };
  const act_2095089031469157_reloadGrid_y8tj3p0kc_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_2095089031469157_close_1rywp4gke_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_q70fmyvps_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateBillingPlan',
    };
  };
  const act_9004123373732307_reloadGrid_ug0zmr9rp_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_9004123373732307_close_dhyvd0zj7_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_9004123373732307_showModal_7tw3z8nd3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BillingListItem',
    };
  };
  const act_397195341670858_reloadGrid_gitvfvdyr_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_397195341670858_close_b2uuc5p9u_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_397195341670858_showModal_ykgf22g0r_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CopyBillingListItems',
      props: {
        sources: _.map(selectedItems.value, 'id'),
      },
    };
  };
  const act_8823104369878821_reloadGrid_b7j81f17m_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'billingList',
    };
  };
  const act_8823104369878821_request_kp54wk0vo_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_many_billing_list_item',
      data: { items: selectedItems.value.map((p) => p.id) },
    };
  };
  const act_7454831470842007_reloadGrid_kxgkh06oj_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_7454831470842007_close_mx59d9quz_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_7454831470842007_showModal_mnozogucf_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BatchBillingListEditor',
      props: {
        items: selectedItems.value,
      },
    };
  };
  const act_2237382557075493_reloadGrid_wbgpumjlr_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_2237382557075493_close_u0zvc87j5_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2237382557075493_showModal_ui2zrjv0d_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'UploadBillingListItems',
    };
  };
  const act_8320016629450276_reloadGrid_i4dkloptt_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_6705964765214521_reloadGrid_4qsifm3e1_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_6705964765214521_request_2c17n0mfk_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'merge_invoices',
      data: {
        items: _.chain(selectedItems.value)
          .filter((p) => _.get(p, 'invoice.id') != null)
          .map((p) => p.invoice.id)
          .uniq()
          .value(),
      },
    };
  };
  const act_6806515264385235_reloadGrid_1q9s5mppg_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_6806515264385235_request_ny6zuh5w1_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_draft_invoices',
      data: {
        invoices: _.chain(selectedItems.value)
          .filter((p) => _.get(p, 'invoice.id') != null)
          .map((p) => p.invoice.id)
          .uniq()
          .value(),
      },
    };
  };

  const act_7315092382398562_setData_6y5xaevsc = new actionTypes.SetDataAction({
    actionArgs: act_7315092382398562_setData_6y5xaevsc_getActionArgs,
  });
  const inst_evh_7315092382398562_selectedRowsChanged_6d2ekor8p =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_setData_6y5xaevsc,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_7315092382398562_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_selectedRowsChanged_6d2ekor8p],
      event: 'selectedRowsChanged',
    });
  const act_7315092382398562_script_scd90432q = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_scd90432q_getActionArgs,
  });
  const inst_evh_7315092382398562_saved_u1vx1ctf2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_scd90432q,
      event: 'saved',
      displayName: 'script',
    });
  const act_7315092382398562_showModal_mcph983y0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7315092382398562_showModal_mcph983y0_getActionArgs,
      events: [inst_evh_7315092382398562_saved_u1vx1ctf2],
    });
  const inst_evh_7315092382398562_command_cozt21l9s =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_showModal_mcph983y0,
      event: 'command',
      when: inst_evh_7315092382398562_command_cozt21l9s_getWhen,
      displayName: 'showModal',
    });
  const act_7315092382398562_script_yfupfl8fg = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_yfupfl8fg_getActionArgs,
  });
  const inst_evh_7315092382398562_saved_3mamuxgz3 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_yfupfl8fg,
      event: 'saved',
      displayName: 'script',
    });
  const act_7315092382398562_showModal_f34z32y95 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7315092382398562_showModal_f34z32y95_getActionArgs,
      events: [inst_evh_7315092382398562_saved_3mamuxgz3],
    });
  const inst_evh_7315092382398562_command_fyxiofnxy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_showModal_f34z32y95,
      event: 'command',
      when: inst_evh_7315092382398562_command_fyxiofnxy_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_7315092382398562_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_7315092382398562_command_cozt21l9s,
      inst_evh_7315092382398562_command_fyxiofnxy,
    ],
    event: 'command',
  });
  const act_2095089031469157_reloadGrid_y8tj3p0kc =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2095089031469157_reloadGrid_y8tj3p0kc_getActionArgs,
    });
  const inst_evh_2095089031469157_close_1rywp4gke =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadGrid_y8tj3p0kc,
      event: 'close',
      when: inst_evh_2095089031469157_close_1rywp4gke_getWhen,
      displayName: 'reloadGrid',
    });
  const act_2095089031469157_showModal_q70fmyvps =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_q70fmyvps_getActionArgs,
      events: [inst_evh_2095089031469157_close_1rywp4gke],
    });
  const inst_evh_2095089031469157_click_9jimv65ug =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_q70fmyvps,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_9jimv65ug],
    event: 'click',
  });
  const act_9004123373732307_reloadGrid_ug0zmr9rp =
    new actionTypes.ReloadGridAction({
      actionArgs: act_9004123373732307_reloadGrid_ug0zmr9rp_getActionArgs,
    });
  const inst_evh_9004123373732307_close_dhyvd0zj7 =
    new actionTypes.EventHandlerImpl({
      action: act_9004123373732307_reloadGrid_ug0zmr9rp,
      event: 'close',
      when: inst_evh_9004123373732307_close_dhyvd0zj7_getWhen,
      displayName: 'reloadGrid',
    });
  const act_9004123373732307_showModal_7tw3z8nd3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_9004123373732307_showModal_7tw3z8nd3_getActionArgs,
      events: [inst_evh_9004123373732307_close_dhyvd0zj7],
    });
  const inst_evh_9004123373732307_click_dpuym2pnn =
    new actionTypes.EventHandlerImpl({
      action: act_9004123373732307_showModal_7tw3z8nd3,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_9004123373732307_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_9004123373732307_click_dpuym2pnn],
    event: 'click',
  });
  const act_397195341670858_reloadGrid_gitvfvdyr =
    new actionTypes.ReloadGridAction({
      actionArgs: act_397195341670858_reloadGrid_gitvfvdyr_getActionArgs,
    });
  const inst_evh_397195341670858_close_b2uuc5p9u =
    new actionTypes.EventHandlerImpl({
      action: act_397195341670858_reloadGrid_gitvfvdyr,
      event: 'close',
      when: inst_evh_397195341670858_close_b2uuc5p9u_getWhen,
      displayName: 'reloadGrid',
    });
  const act_397195341670858_showModal_ykgf22g0r =
    new actionTypes.ShowModalAction({
      actionArgs: act_397195341670858_showModal_ykgf22g0r_getActionArgs,
      events: [inst_evh_397195341670858_close_b2uuc5p9u],
    });
  const inst_evh_397195341670858_click_6e18dpycs =
    new actionTypes.EventHandlerImpl({
      action: act_397195341670858_showModal_ykgf22g0r,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_397195341670858_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_397195341670858_click_6e18dpycs],
    event: 'click',
  });
  const act_8823104369878821_reloadGrid_b7j81f17m =
    new actionTypes.ReloadGridAction({
      actionArgs: act_8823104369878821_reloadGrid_b7j81f17m_getActionArgs,
    });
  const inst_evh_8823104369878821_success_7kgums1lp =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_reloadGrid_b7j81f17m,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_8823104369878821_request_kp54wk0vo = new actionTypes.RequestAction({
    actionArgs: act_8823104369878821_request_kp54wk0vo_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_8823104369878821_success_7kgums1lp],
  });
  const inst_evh_8823104369878821_click_f7heqnzs7 =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_request_kp54wk0vo,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_8823104369878821_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8823104369878821_click_f7heqnzs7],
    event: 'click',
  });
  const act_7454831470842007_reloadGrid_kxgkh06oj =
    new actionTypes.ReloadGridAction({
      actionArgs: act_7454831470842007_reloadGrid_kxgkh06oj_getActionArgs,
    });
  const inst_evh_7454831470842007_close_mx59d9quz =
    new actionTypes.EventHandlerImpl({
      action: act_7454831470842007_reloadGrid_kxgkh06oj,
      event: 'close',
      when: inst_evh_7454831470842007_close_mx59d9quz_getWhen,
      displayName: 'reloadGrid',
    });
  const act_7454831470842007_showModal_mnozogucf =
    new actionTypes.ShowModalAction({
      actionArgs: act_7454831470842007_showModal_mnozogucf_getActionArgs,
      events: [inst_evh_7454831470842007_close_mx59d9quz],
    });
  const inst_evh_7454831470842007_click_5fma0i91h =
    new actionTypes.EventHandlerImpl({
      action: act_7454831470842007_showModal_mnozogucf,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_7454831470842007_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7454831470842007_click_5fma0i91h],
    event: 'click',
  });
  const act_2237382557075493_reloadGrid_wbgpumjlr =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2237382557075493_reloadGrid_wbgpumjlr_getActionArgs,
    });
  const inst_evh_2237382557075493_close_u0zvc87j5 =
    new actionTypes.EventHandlerImpl({
      action: act_2237382557075493_reloadGrid_wbgpumjlr,
      event: 'close',
      when: inst_evh_2237382557075493_close_u0zvc87j5_getWhen,
      displayName: 'reloadGrid',
    });
  const act_2237382557075493_showModal_ui2zrjv0d =
    new actionTypes.ShowModalAction({
      actionArgs: act_2237382557075493_showModal_ui2zrjv0d_getActionArgs,
      events: [inst_evh_2237382557075493_close_u0zvc87j5],
    });
  const inst_evh_2237382557075493_click_3exe4fc41 =
    new actionTypes.EventHandlerImpl({
      action: act_2237382557075493_showModal_ui2zrjv0d,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2237382557075493_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2237382557075493_click_3exe4fc41],
    event: 'click',
  });
  const act_8320016629450276_reloadGrid_i4dkloptt =
    new actionTypes.ReloadGridAction({
      actionArgs: act_8320016629450276_reloadGrid_i4dkloptt_getActionArgs,
    });
  const inst_evh_8320016629450276_success_dlbx4rohk =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadGrid_i4dkloptt,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const inst_evh_8320016629450276_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8320016629450276_success_dlbx4rohk],
    event: 'success',
  });
  const act_6705964765214521_reloadGrid_4qsifm3e1 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6705964765214521_reloadGrid_4qsifm3e1_getActionArgs,
    });
  const inst_evh_6705964765214521_success_udvzubfx8 =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_reloadGrid_4qsifm3e1,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_6705964765214521_request_2c17n0mfk = new actionTypes.RequestAction({
    actionArgs: act_6705964765214521_request_2c17n0mfk_getActionArgs,
    displayName: 'mergeInvoices',
    events: [inst_evh_6705964765214521_success_udvzubfx8],
  });
  const inst_evh_6705964765214521_click_l3zhzhkhh =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_request_2c17n0mfk,
      event: 'click',
      displayName: 'mergeInvoices',
    });
  const inst_evh_6705964765214521_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_click_l3zhzhkhh],
    event: 'click',
  });
  const act_6806515264385235_reloadGrid_1q9s5mppg =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6806515264385235_reloadGrid_1q9s5mppg_getActionArgs,
    });
  const inst_evh_6806515264385235_success_243ymyww9 =
    new actionTypes.EventHandlerImpl({
      action: act_6806515264385235_reloadGrid_1q9s5mppg,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_6806515264385235_request_ny6zuh5w1 = new actionTypes.RequestAction({
    actionArgs: act_6806515264385235_request_ny6zuh5w1_getActionArgs,
    displayName: 'deleteInvoices',
    events: [inst_evh_6806515264385235_success_243ymyww9],
  });
  const inst_evh_6806515264385235_click_a7cmwm0fp =
    new actionTypes.EventHandlerImpl({
      action: act_6806515264385235_request_ny6zuh5w1,
      event: 'click',
      displayName: 'deleteInvoices',
    });
  const inst_evh_6806515264385235_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6806515264385235_click_a7cmwm0fp],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_selectedRowsChanged_6d2ekor8p,
    inst_evh_7315092382398562_selectedRowsChanged,
    inst_evh_7315092382398562_saved_u1vx1ctf2,
    inst_evh_7315092382398562_command_cozt21l9s,
    inst_evh_7315092382398562_saved_3mamuxgz3,
    inst_evh_7315092382398562_command_fyxiofnxy,
    inst_evh_7315092382398562_command,
    inst_evh_2095089031469157_close_1rywp4gke,
    inst_evh_2095089031469157_click_9jimv65ug,
    inst_evh_2095089031469157_click,
    inst_evh_9004123373732307_close_dhyvd0zj7,
    inst_evh_9004123373732307_click_dpuym2pnn,
    inst_evh_9004123373732307_click,
    inst_evh_397195341670858_close_b2uuc5p9u,
    inst_evh_397195341670858_click_6e18dpycs,
    inst_evh_397195341670858_click,
    inst_evh_8823104369878821_success_7kgums1lp,
    inst_evh_8823104369878821_click_f7heqnzs7,
    deleteItems: inst_evh_8823104369878821_click_f7heqnzs7,
    inst_evh_8823104369878821_click,
    inst_evh_7454831470842007_close_mx59d9quz,
    inst_evh_7454831470842007_click_5fma0i91h,
    inst_evh_7454831470842007_click,
    inst_evh_2237382557075493_close_u0zvc87j5,
    inst_evh_2237382557075493_click_3exe4fc41,
    inst_evh_2237382557075493_click,
    inst_evh_8320016629450276_success_dlbx4rohk,
    inst_evh_8320016629450276_success,
    inst_evh_6705964765214521_success_udvzubfx8,
    inst_evh_6705964765214521_click_l3zhzhkhh,
    mergeInvoices: inst_evh_6705964765214521_click_l3zhzhkhh,
    inst_evh_6705964765214521_click,
    inst_evh_6806515264385235_success_243ymyww9,
    inst_evh_6806515264385235_click_a7cmwm0fp,
    deleteInvoices: inst_evh_6806515264385235_click_a7cmwm0fp,
    inst_evh_6806515264385235_click,
  };

  const evh_7315092382398562_selectedRowsChanged_6d2ekor8p = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_selectedRowsChanged_6d2ekor8p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_saved_u1vx1ctf2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_saved_u1vx1ctf2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command_cozt21l9s = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command_cozt21l9s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_saved_3mamuxgz3 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_saved_3mamuxgz3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command_fyxiofnxy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command_fyxiofnxy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_1rywp4gke = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_1rywp4gke.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_9jimv65ug = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_9jimv65ug.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_close_dhyvd0zj7 = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_close_dhyvd0zj7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_click_dpuym2pnn = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_click_dpuym2pnn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_click = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_close_b2uuc5p9u = async (event, scope) => {
    return await actions.inst_evh_397195341670858_close_b2uuc5p9u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_click_6e18dpycs = async (event, scope) => {
    return await actions.inst_evh_397195341670858_click_6e18dpycs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_click = async (event, scope) => {
    return await actions.inst_evh_397195341670858_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_success_7kgums1lp = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_success_7kgums1lp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_click_f7heqnzs7 = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_click_f7heqnzs7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_click = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7454831470842007_close_mx59d9quz = async (event, scope) => {
    return await actions.inst_evh_7454831470842007_close_mx59d9quz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7454831470842007_click_5fma0i91h = async (event, scope) => {
    return await actions.inst_evh_7454831470842007_click_5fma0i91h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7454831470842007_click = async (event, scope) => {
    return await actions.inst_evh_7454831470842007_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_close_u0zvc87j5 = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_close_u0zvc87j5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_click_3exe4fc41 = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_click_3exe4fc41.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2237382557075493_click = async (event, scope) => {
    return await actions.inst_evh_2237382557075493_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success_dlbx4rohk = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success_dlbx4rohk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_success_udvzubfx8 = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_success_udvzubfx8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click_l3zhzhkhh = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click_l3zhzhkhh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_success_243ymyww9 = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_success_243ymyww9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_click_a7cmwm0fp = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_click_a7cmwm0fp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6806515264385235_click = async (event, scope) => {
    return await actions.inst_evh_6806515264385235_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpDataGrid
    name="billingList"
    :headerTitle="messages.title"
    :items="ds"
    checkbox-row-selection
    export-to-csv
    :exportToCsvFilename="messages.exportFilename"
    :columnDefaults="b_7315092382398562_columnDefaults"
    :columns="b_7315092382398562_columns"
    ref="grid"
    @selectedRowsChanged="evh_7315092382398562_selectedRowsChanged($event, {})"
    @command="evh_7315092382398562_command($event, {})"
  >
    <template #toolbar>
      <div class="flex-grow-1 d-flex gap-2">
        <BDropdown size="sm" toggle-class="btn-light border" strategy="fixed">
          <template #button-content>
            <ItpIcon :icon="$config.billingList.createItemIcon" class="mx-1">
            </ItpIcon>
            <ItpText :text="pgettext('BillingList', 'Billing plan')"> </ItpText>
          </template>
          <ItpNavItem
            :icon="$config.billingList.createItemIcon"
            :text="pgettext('BillingList', 'Create billing plan...')"
            size="sm"
            dropdown-item
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :icon="$config.billingList.createItemIcon"
            :text="pgettext('BillingList', 'Add item...')"
            size="sm"
            dropdown-item
            @click="evh_9004123373732307_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :icon="$config.billingList.createItemIcon"
            :text="pgettext('BillingList', 'Copy items...')"
            size="sm"
            :disabled="!selectedItems || !selectedItems.length"
            dropdown-item
            @click="evh_397195341670858_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            icon="fa-trash"
            :text="gettext('Delete')"
            size="sm"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            dropdown-item
            @click="evh_8823104369878821_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :icon="$config.billingList.createItemIcon"
            :text="pgettext('BillingList', 'Bulk edit...')"
            size="sm"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            dropdown-item
            @click="evh_7454831470842007_click($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :icon="$config.uploadIcon"
            :text="pgettext('BillingList', 'Bulk upload...')"
            size="sm"
            dropdown-item
            @click="evh_2237382557075493_click($event, {})"
          >
          </ItpNavItem>
        </BDropdown>
        <JobHandlerControl
          size="sm"
          jobType="invoice_generation_from_billing_list"
          load-current
          :payload="onCreateInvoicesPayload"
          icon="mdi:cog"
          :text="pgettext('BillingList|Submit', 'Generate invoices')"
          :disabled="!selectedItems || !selectedItems.length"
          @success="evh_8320016629450276_success($event, {})"
        >
        </JobHandlerControl>
        <ItpButton
          :icon="$config.mergeIcon"
          :text="gettext('Merge invoices')"
          size="sm"
          variant="light"
          :disabled="!selectedItems || !selectedItems.length"
          :spinning="actions.mergeInvoices.isRunning"
          @click="evh_6705964765214521_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          icon="fa-trash"
          :text="gettext('Delete invoices')"
          size="sm"
          variant="light"
          :disabled="!selectedItems || !selectedItems.length"
          :spinning="actions.deleteInvoices.isRunning"
          @click="evh_6806515264385235_click($event, {})"
        >
        </ItpButton>
        <ArtifactJob
          icon="ic:baseline-download"
          :text="gettext('Download views')"
          size="sm"
          :disabled="!selectedItems || !selectedItems.length"
          operation="download_invoice_views"
          :payload="getDownloadViewsPayload"
        >
        </ArtifactJob>
      </div>
    </template>
  </ItpDataGrid>
</template>
